import React, { useEffect } from 'react'

import { useNavigate } from 'react-router-dom'

import { useConfig } from '@client/contexts/ConfigProvider'
import { useEvents } from '@contexts/GTMProvider'
import { useUserDetailsQuery } from '@hooks/api'
import { isApp } from '@shared/platform'

export default function NativeAppInitialiser({ children }: { children: React.ReactNode }): React.JSX.Element {

  const navigate = useNavigate()
  const events = useEvents()
  const config = useConfig()
  const { data: userDetailsData } = useUserDetailsQuery({ ssr: config.fetchSSRQuery() })

  const extractClickUrl = (notif: any) => {
    return (
      // the dashboard “Launch URL”
      notif?.launchURL ||
      // if you set a custom deep link key
      notif?.additionalData?.deeplink ||
      notif?.additionalData?.url
    )
  }

  const toPath = (url: string, coldStart: boolean): string => {
    try {
      const u = new URL(url)
      const route = u.pathname + u.search + u.hash

      events?.hasOpenedAppWithDeeplink(userDetailsData.currentUser.id ?? 'anonymous', coldStart, url)
      return route
    } catch (error) {
      events?.hasOpenedAppWithDeeplinkError(userDetailsData.currentUser.id ?? 'anonymous', coldStart, url, error as Error)
      return '/'
    }
  }

  const initDeeplinks = async () => {
    const { App } = await import('@capacitor/app')

    App.addListener('appUrlOpen', (event) => {
      if (event?.url) {
        navigate(toPath(event.url, false))
      }

      // if no match, do nothing - let regular routing logic take over
    })

    const launchUrl = await App.getLaunchUrl()

    if (launchUrl?.url) {
      navigate(toPath(launchUrl.url, true))
    }
  }

  const initSocialLogin = async () => {
    const { SocialLogin } = await import('@capgo/capacitor-social-login')

    if (!config.getGoogle().clientId) {
      console.warn('GOOGLE_CLIENT_ID is not set. Google social login will not work.')
      return
    }

    if (!config.getGoogle().iosClientId) {
      console.warn('GOOGLE_IOS_CLIENT_ID is not set. Google social login will not work.')
      return
    }

    if (!config.getFacebook().appId) {
      console.warn('FACEBOOK_APP_ID is not set. Facebook social login will not work.')
      return
    }

    if (!config.getFacebook().clientToken) {
      console.warn('FACEBOOK_CLIENT_TOKEN is not set. Facebook social login will not work.')
      return
    }

    const suffix = '.apps.googleusercontent.com'
    const ensureSuffix = (id: string) => id.endsWith(suffix) ? id : `${id}${suffix}`

    console.log('Initializing Capacitor social login')

    await SocialLogin.initialize({
        apple: {},
        google: {
          webClientId: ensureSuffix(config.getGoogle().clientId),
          iOSClientId: ensureSuffix(config.getGoogle().iosClientId),
        },
        facebook: {
          appId: config.getFacebook().appId,
          clientToken: config.getFacebook().clientToken,
        },
    })
  }

  const initCapacitorOneSignal = async () => {
    if (!config.getOneSignal()) {
      console.warn('ONE_SIGNAL_APP_ID is not set. Capacitor OneSignal will not work.')
      return
    }

    const OneSignal = (await import('onesignal-cordova-plugin')).default

    console.log('Initializing Capacitor OneSignal')

    OneSignal.initialize(config.getOneSignal())

    OneSignal.Notifications.addEventListener('click', (event) => {
      const url = extractClickUrl(event.notification)

      console.log('NOTIF CLICKED', url, JSON.stringify(event.notification))

      if (url) {
        navigate(toPath(url, false))
      }
    })

    OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
      const url = extractClickUrl(event.getNotification())

      console.log('IN APP NOTIF', url, JSON.stringify(event.getNotification()))
    })

    setTimeout(async () => {
      const granted = await OneSignal.Notifications.requestPermission(false)
      console.log('Push permission status:', granted)

      if (granted) {
        const isOptedIn = await OneSignal.User.pushSubscription.getOptedInAsync()

        if (!isOptedIn) OneSignal.User.pushSubscription.optIn()
      } else {
        OneSignal.User.pushSubscription.optOut()
      }
    }, 10000)
  }

  useEffect(() => {
    if (navigator.userAgent.includes('ucookCapApp') && isApp()) {
      initDeeplinks()
      initCapacitorOneSignal()
      initSocialLogin()
    }
  }, [])

  return <>{ children }</>
}
